import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Select,
  Row,
  Col,
  Typography,
  message,
  Spin,
  Modal,
  Descriptions,
  Badge,
  Empty,
  Divider,
  Input,
  Upload,
  Form,
  Popconfirm,
  Collapse
} from 'antd';
import {
  CheckCircleOutlined,
  FileTextOutlined,
  MailOutlined,
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  PhoneOutlined,
  HomeOutlined,
  TeamOutlined,
  FilePdfOutlined,
  SearchOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useCognitoAuth } from '../contexts/CognitoAuthContext';
import apiService from '../services/api';
import LeadDocumentsModal from './LeadDocumentsModal';
import EmailModal from './EmailModal';
import ContratoAdesaoNOSReviewModal from './ContratoAdesaoNOSReviewModal';
import CustomToggle from './CustomToggle';
import AlticeSaleDetailView from './AlticeSaleDetailView';
import { Lead } from '../services/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// Service names mapping for energy proposals
const SERVICE_NAMES: Record<string, string> = {
  'bairro-solar': 'Bairro Solar',
  'pap': 'Pay AS Produced (PAP)',
  'transacional': 'Transacional',
  'luz': 'Luz',
  'gas': 'Gás',
  'mobilidade': 'Mobilidade Elétrica',
  'outro': 'Outro'
};

interface TeamValidationData {
  validation_leads: Array<{
    id: number;
    name: string;
    email: string;
    phone: string;
    address: string;
    city: string;
    lead_status: string;
    section: string;
    assigned_to: number | null;
    assigned_to_username: string | null;
    created_by: number;
    created_by_username: string | null;
    updated_at: string;
    created_at: string;
  }>;
  nos_contratos: Array<any>;
  altice_forms: Array<any>;
  energy_proposals: Array<any>;
  prosegur_forms: Array<any>;
  team_members: Array<{
    id: number;
    username: string;
    email: string;
    role: string;
  }>;
  team_leader: {
    id: number;
    username: string;
    role: string;
  };
}

interface TeamLeader {
  id: number;
  username: string;
  email: string;
  role: string;
  team_id: number | null;
  team_name: string | null;
  member_count: number;
}

const TeamValidationProcessing: React.FC = () => {
  const { user } = useCognitoAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<TeamValidationData | null>(null);
  const [teamLeaders, setTeamLeaders] = useState<TeamLeader[]>([]);
  const [selectedTeamLeaderId, setSelectedTeamLeaderId] = useState<number | null>(null);
  const [activePanels, setActivePanels] = useState<string[]>(['validation']);
  const [searchText, setSearchText] = useState('');
  
  // Toggle states for marking items as done
  const [validationLeadsDone, setValidationLeadsDone] = useState<Record<number, boolean>>({});
  const [nosContratosDone, setNosContratosDone] = useState<Record<number, boolean>>({});
  const [nosSecuritasContratosDone, setNosSecuritasContratosDone] = useState<Record<number, boolean>>({});
  const [alticeFormsDone, setAlticeFormsDone] = useState<Record<number, boolean>>({});
  const [energyProposalsDone, setEnergyProposalsDone] = useState<Record<number, boolean>>({});
  const [prosegurFormsDone, setProsegurFormsDone] = useState<Record<number, boolean>>({});

  // Modal states
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  const [selectedLeadForDocuments, setSelectedLeadForDocuments] = useState<Lead | null>(null);
  const [emailModalVisible, setEmailModalVisible] = useState(false);
  const [selectedLeadForEmail, setSelectedLeadForEmail] = useState<Lead | null>(null);
  const [nosDetailModalVisible, setNosDetailModalVisible] = useState(false);
  const [selectedNosContrato, setSelectedNosContrato] = useState<any | null>(null);
  const [alticeDetailModalVisible, setAlticeDetailModalVisible] = useState(false);
  const [selectedAlticeForm, setSelectedAlticeForm] = useState<any | null>(null);

  // NOS Email Modal states
  const [nosEmailModalVisible, setNosEmailModalVisible] = useState(false);
  const [selectedNosForEmail, setSelectedNosForEmail] = useState<any | null>(null);
  
  // Energy Proposal Detail Modal states
  const [energyProposalDetailModalVisible, setEnergyProposalDetailModalVisible] = useState(false);
  const [selectedEnergyProposal, setSelectedEnergyProposal] = useState<any | null>(null);
  
  // Prosegur Form Detail Modal states
  const [prosegurFormDetailModalVisible, setProsegurFormDetailModalVisible] = useState(false);
  const [selectedProsegurForm, setSelectedProsegurForm] = useState<any | null>(null);

  // Check if user can select team leaders (Admin/Diretor Comercial)
  const canSelectTeamLeader = user && (
    user.role === 'Admin' || user.role === 'admin' ||
    user.role === 'Diretor Comercial' || user.role === 'diretor comercial'
  );

  useEffect(() => {
    if (canSelectTeamLeader) {
      fetchTeamLeaders();
    }
    fetchData();
    loadToggleStates();
  }, [selectedTeamLeaderId, canSelectTeamLeader]);

  // Load toggle states from localStorage
  const loadToggleStates = () => {
    try {
      const savedValidationLeads = JSON.parse(localStorage.getItem('teamValidation_leadsDone') || '{}');
      const savedNosContratos = JSON.parse(localStorage.getItem('teamValidation_nosContratosDone') || '{}');
      const savedNosSecuritasContratos = JSON.parse(localStorage.getItem('teamValidation_nosSecuritasContratosDone') || '{}');
      const savedAlticeForms = JSON.parse(localStorage.getItem('teamValidation_alticeFormsDone') || '{}');
      const savedEnergyProposals = JSON.parse(localStorage.getItem('teamValidation_energyProposalsDone') || '{}');
      const savedProsegurForms = JSON.parse(localStorage.getItem('teamValidation_prosegurFormsDone') || '{}');
      
      setValidationLeadsDone(savedValidationLeads);
      setNosContratosDone(savedNosContratos);
      setNosSecuritasContratosDone(savedNosSecuritasContratos);
      setAlticeFormsDone(savedAlticeForms);
      setEnergyProposalsDone(savedEnergyProposals);
      setProsegurFormsDone(savedProsegurForms);
    } catch (error) {
      console.error('Error loading toggle states:', error);
    }
  };

  // Toggle handlers
  const handleValidationLeadToggle = (leadId: number, checked: boolean) => {
    setValidationLeadsDone(prev => {
      const updated = { ...prev, [leadId]: checked };
      localStorage.setItem('teamValidation_leadsDone', JSON.stringify(updated));
      return updated;
    });
  };

  const handleNosContratoToggle = (contratoId: number, checked: boolean) => {
    setNosContratosDone(prev => {
      const updated = { ...prev, [contratoId]: checked };
      localStorage.setItem('teamValidation_nosContratosDone', JSON.stringify(updated));
      return updated;
    });
  };

  const handleNosSecuritasContratoToggle = (contratoId: number, checked: boolean) => {
    setNosSecuritasContratosDone(prev => {
      const updated = { ...prev, [contratoId]: checked };
      localStorage.setItem('teamValidation_nosSecuritasContratosDone', JSON.stringify(updated));
      return updated;
    });
  };

  const handleAlticeFormToggle = (formId: number, checked: boolean) => {
    setAlticeFormsDone(prev => {
      const updated = { ...prev, [formId]: checked };
      localStorage.setItem('teamValidation_alticeFormsDone', JSON.stringify(updated));
      return updated;
    });
  };

  const handleEnergyProposalToggle = (proposalId: number, checked: boolean) => {
    setEnergyProposalsDone(prev => {
      const updated = { ...prev, [proposalId]: checked };
      localStorage.setItem('teamValidation_energyProposalsDone', JSON.stringify(updated));
      return updated;
    });
  };

  const handleProsegurFormToggle = (formId: number, checked: boolean) => {
    setProsegurFormsDone(prev => {
      const updated = { ...prev, [formId]: checked };
      localStorage.setItem('teamValidation_prosegurFormsDone', JSON.stringify(updated));
      return updated;
    });
  };

  const fetchTeamLeaders = async () => {
    try {
      const leaders = await apiService.getTeamLeaders();
      setTeamLeaders(leaders);
    } catch (error) {
      message.error('Erro ao carregar líderes de equipa');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await apiService.getTeamValidationData(selectedTeamLeaderId || undefined);
      if (result.success) {
        // Ensure energy_proposals exists (for backward compatibility)
        setData({
          ...result,
          energy_proposals: result.energy_proposals || [],
          prosegur_forms: result.prosegur_forms || []
        });
      } else {
        message.error('Erro ao carregar dados');
      }
    } catch (error) {
      message.error('Erro ao carregar dados de validação');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocuments = (lead: any) => {
    const leadObj: Lead = {
      id: lead.id,
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      address: lead.address,
      city: lead.city,
      lead_status: lead.lead_status,
      section: lead.section
    } as Lead;
    setSelectedLeadForDocuments(leadObj);
    setDocumentsModalVisible(true);
  };

  const handleSendEmail = (lead: any) => {
    const leadObj: Lead = {
      id: lead.id,
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      address: lead.address,
      city: lead.city,
      lead_status: lead.lead_status,
      section: lead.section
    } as Lead;
    setSelectedLeadForEmail(leadObj);
    setEmailModalVisible(true);
  };

  const handleViewNosContrato = (contrato: any) => {
    // Debug: Log the contract data to see what we're receiving
    console.log('Viewing NOS Contrato:', contrato);
    console.log('Is Securitas:', contrato.is_securitas);
    console.log('Securitas Fields:', contrato.securitas_fields);
    console.log('Pacote Comercial:', contrato.pacote_comercial);
    console.log('Outros Equipamentos:', contrato.outros_equipamentos);
    setSelectedNosContrato(contrato);
    setNosDetailModalVisible(true);
  };

  const handleSendNosEmail = async (contrato: any) => {
    setSelectedNosForEmail(contrato);
    setNosEmailModalVisible(true);
  };

  const handleViewAlticeForm = (form: any) => {
    setSelectedAlticeForm(form);
    setAlticeDetailModalVisible(true);
  };

  const handleViewEnergyProposal = (proposal: any) => {
    setSelectedEnergyProposal(proposal);
    setEnergyProposalDetailModalVisible(true);
  };

  const handleViewProsegurForm = (form: any) => {
    setSelectedProsegurForm(form);
    setProsegurFormDetailModalVisible(true);
  };

  const handleApproveLead = async (leadId: number) => {
    try {
      // Update the lead status to "Por Instalar" and move to instalados section
      const response = await apiService.updateLead(leadId, {
        lead_status: 'Por Instalar',
        section: 'instalados'
      });
      
      if (response) {
        message.success('Lead aprovado e movido para Instalados');
        // Refresh the data
        fetchData();
      }
    } catch (error) {
      message.error('Erro ao aprovar lead');
    }
  };

  const handleRejectLead = async (leadId: number) => {
    try {
      // Update the lead status to "Sem Interesse" and move back to feedback section
      const response = await apiService.updateLead(leadId, {
        lead_status: 'Sem Interesse',
        section: 'feedback'
      });
      
      if (response) {
        message.success('Lead rejeitado e movido para Feedback');
        // Refresh the data
        fetchData();
      }
    } catch (error) {
      message.error('Erro ao rejeitar lead');
    }
  };

  // Filter data based on search text
  const filterData = (items: any[], searchFields: string[]) => {
    if (!searchText.trim()) return items;
    const searchLower = searchText.toLowerCase();
    return items.filter(item =>
      searchFields.some(field => {
        const value = item[field];
        return value && value.toString().toLowerCase().includes(searchLower);
      })
    );
  };

  const filteredValidationLeads = data ? filterData(data.validation_leads, ['name', 'email', 'phone', 'assigned_to_username', 'created_by_username']) : [];
  
  // Separate NOS and NOS Securitas contracts
  const allNosContratos = data ? filterData(data.nos_contratos, ['nome_cliente', 'lead_name', 'user_username']) : [];
  const filteredNosContratos = allNosContratos.filter(contrato => !contrato.is_securitas);
  const filteredNosSecuritasContratos = allNosContratos.filter(contrato => contrato.is_securitas);
  
  const filteredAlticeForms = data ? filterData(data.altice_forms, ['nome', 'lead_name', 'creator_username']) : [];
  const filteredEnergyProposals = data ? filterData(data.energy_proposals || [], ['lead_name', 'creator_username']) : [];
  const filteredProsegurForms = data ? filterData(data.prosegur_forms || [], ['lead_name', 'creator_username', 'nome', 'nif']) : [];

  // Sort by toggle status (off first, then on)
  const sortByToggleStatus = <T extends { id: number }>(items: T[], doneState: Record<number, boolean>): T[] => {
    return [...items].sort((a, b) => {
      const aDone = doneState[a.id] || false;
      const bDone = doneState[b.id] || false;
      // Off (false) comes first, then on (true)
      if (aDone === bDone) return 0;
      return aDone ? 1 : -1;
    });
  };

  const sortedValidationLeads = sortByToggleStatus(filteredValidationLeads, validationLeadsDone);
  const sortedNosContratos = sortByToggleStatus(filteredNosContratos, nosContratosDone);
  const sortedNosSecuritasContratos = sortByToggleStatus(filteredNosSecuritasContratos, nosSecuritasContratosDone);
  const sortedAlticeForms = sortByToggleStatus(filteredAlticeForms, alticeFormsDone);
  const sortedEnergyProposals = sortByToggleStatus(filteredEnergyProposals, energyProposalsDone);
  const sortedProsegurForms = sortByToggleStatus(filteredProsegurForms, prosegurFormsDone);

  const renderValidationLeads = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!data || sortedValidationLeads.length === 0) {
      return (
        <Empty
          description="Nenhum lead em validação encontrado"
          style={{ padding: '40px' }}
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {sortedValidationLeads.map((lead) => (
          <Col xs={24} sm={12} lg={8} key={lead.id}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <Title level={5} style={{ margin: 0, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                    {lead.name}
                  </Title>
                  <CustomToggle
                    id={`validation-lead-${lead.id}`}
                    checked={validationLeadsDone[lead.id] || false}
                    onChange={(checked) => handleValidationLeadToggle(lead.id, checked)}
                  />
                </div>
              </div>
              
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {lead.assigned_to_username || lead.created_by_username || 'N/A'}
                  </Text>
                </div>
                {lead.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      {lead.email}
                    </Text>
                  </div>
                )}
                {lead.phone && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      {lead.phone}
                    </Text>
                  </div>
                )}
                {lead.address && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <HomeOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      {lead.address}, {lead.city}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {new Date(lead.updated_at).toLocaleDateString('pt-PT')}
                  </Text>
                </div>
              </Space>

              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space style={{ width: '100%' }} size="small">
                  <Button
                    icon={<FilePdfOutlined />}
                    onClick={() => handleViewDocuments(lead)}
                    style={{ flex: 1 }}
                  >
                    Documentos
                  </Button>
                  <Button
                    icon={<MailOutlined />}
                    onClick={() => handleSendEmail(lead)}
                    style={{ flex: 1 }}
                  >
                    Email
                  </Button>
                </Space>
                <Space style={{ width: '100%' }} size="small">
                  <Popconfirm
                    title={
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>Aprovar Lead</div>
                        <div>Tem a certeza que deseja aprovar este lead? O lead será movido para a secção 'Instalados'.</div>
                      </div>
                    }
                    onConfirm={() => handleApproveLead(lead.id)}
                    okText="Sim, Aprovar"
                    cancelText="Cancelar"
                    okButtonProps={{ type: 'primary' }}
                  >
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      style={{ 
                        flex: 1,
                        background: '#52c41a',
                        borderColor: '#52c41a',
                        fontWeight: 500
                      }}
                    >
                      Aprovar
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '8px' }}>Rejeitar Lead</div>
                        <div>Tem a certeza que deseja rejeitar este lead? O lead será movido para a secção 'Feedback'.</div>
                      </div>
                    }
                    onConfirm={() => handleRejectLead(lead.id)}
                    okText="Sim, Rejeitar"
                    cancelText="Cancelar"
                    okButtonProps={{ type: 'primary', danger: true }}
                  >
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      style={{ 
                        flex: 1,
                        fontWeight: 500
                      }}
                    >
                      Rejeitar
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderNosContratos = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!data || sortedNosContratos.length === 0) {
      return (
        <Empty
          description="Nenhum contrato de adesão NOS encontrado"
          style={{ padding: '40px' }}
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {sortedNosContratos.map((contrato) => (
          <Col xs={24} sm={12} lg={12} xl={8} key={contrato.id}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                height: '100%'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <Title level={5} style={{ margin: 0, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                      {contrato.nome_cliente || contrato.lead_name || 'N/A'}
                    </Title>
                    <Tag color="blue" style={{ margin: 0 }}>
                      NOS
                    </Tag>
                  </div>
                  <CustomToggle
                    id={`nos-contrato-${contrato.id}`}
                    checked={nosContratosDone[contrato.id] || false}
                    onChange={(checked) => handleNosContratoToggle(contrato.id, checked)}
                  />
                </div>
                {contrato.status && (
                  <Tag color={contrato.status === 'pending' ? 'orange' : contrato.status === 'approved' ? 'green' : 'red'}>
                    {contrato.status === 'pending' ? 'Pendente' : contrato.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                  </Tag>
                )}
              </div>
              
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {contrato.user_username || 'N/A'}
                  </Text>
                </div>
                {contrato.lead_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Lead: {contrato.lead_name}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {new Date(contrato.created_at).toLocaleDateString('pt-PT')}
                  </Text>
                </div>
              </Space>

              <Space style={{ width: '100%' }} size="small">
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => handleViewNosContrato(contrato)}
                  style={{ flex: 1 }}
                >
                  Ver Detalhes
                </Button>
                <Button
                  icon={<MailOutlined />}
                  onClick={() => handleSendNosEmail(contrato)}
                  style={{ flex: 1 }}
                >
                  Email
                </Button>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderNosSecuritasContratos = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!data || sortedNosSecuritasContratos.length === 0) {
      return (
        <Empty
          description="Nenhum contrato de adesão NOS Securitas encontrado"
          style={{ padding: '40px' }}
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {sortedNosSecuritasContratos.map((contrato) => (
          <Col xs={24} sm={12} lg={12} xl={8} key={contrato.id}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                height: '100%'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                    <Title level={5} style={{ margin: 0, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                      {contrato.nome_cliente || contrato.lead_name || 'N/A'}
                    </Title>
                    <Tag color="purple" style={{ margin: 0 }}>
                      NOS Securitas
                    </Tag>
                  </div>
                  <CustomToggle
                    id={`nos-securitas-contrato-${contrato.id}`}
                    checked={nosSecuritasContratosDone[contrato.id] || false}
                    onChange={(checked) => handleNosSecuritasContratoToggle(contrato.id, checked)}
                  />
                </div>
                {contrato.status && (
                  <Tag color={contrato.status === 'pending' ? 'orange' : contrato.status === 'approved' ? 'green' : 'red'}>
                    {contrato.status === 'pending' ? 'Pendente' : contrato.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                  </Tag>
                )}
              </div>
              
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {contrato.user_username || 'N/A'}
                  </Text>
                </div>
                {contrato.lead_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Lead: {contrato.lead_name}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {new Date(contrato.created_at).toLocaleDateString('pt-PT')}
                  </Text>
                </div>
              </Space>

              <Space style={{ width: '100%' }} size="small">
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => handleViewNosContrato(contrato)}
                  style={{ flex: 1 }}
                >
                  Ver Detalhes
                </Button>
                <Button
                  icon={<MailOutlined />}
                  onClick={() => handleSendNosEmail(contrato)}
                  style={{ flex: 1 }}
                >
                  Email
                </Button>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderAlticeForms = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!data || sortedAlticeForms.length === 0) {
      return (
        <Empty
          description="Nenhum formulário Altice encontrado"
          style={{ padding: '40px' }}
        />
      );
    }

    // Helper function to render field if it exists
    const renderField = (label: string, value: any) => {
      if (value === null || value === undefined || value === '') return null;
      return (
        <Descriptions.Item label={label}>
          {String(value)}
        </Descriptions.Item>
      );
    };

    return (
      <Row gutter={[16, 16]}>
        {sortedAlticeForms.map((form) => (
          <Col xs={24} sm={12} lg={12} xl={8} key={form.id}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                height: '100%'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={5} style={{ margin: 0, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                    {form.nome || form.lead_name || 'N/A'}
                  </Title>
                  <CustomToggle
                    id={`altice-form-${form.id}`}
                    checked={alticeFormsDone[form.id] || false}
                    onChange={(checked) => handleAlticeFormToggle(form.id, checked)}
                  />
                </div>
              </div>
              
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {form.creator_username || 'N/A'}
                  </Text>
                </div>
                {form.lead_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Lead: {form.lead_name}
                    </Text>
                  </div>
                )}
                {form.telefone_contacto && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      {form.telefone_contacto}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {new Date(form.created_at).toLocaleDateString('pt-PT')}
                  </Text>
                </div>
              </Space>

              <Button
                icon={<EyeOutlined />}
                onClick={() => handleViewAlticeForm(form)}
                style={{ width: '100%' }}
              >
                Ver Detalhes
              </Button>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderEnergyProposals = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!data || sortedEnergyProposals.length === 0) {
      return (
        <Empty
          description="Nenhum pedido de proposta energia/solar encontrado"
          style={{ padding: '40px' }}
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {sortedEnergyProposals.map((proposal) => (
          <Col xs={24} sm={12} lg={12} xl={8} key={proposal.id}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                height: '100%'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={5} style={{ margin: 0, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                    {proposal.lead_name || (proposal.empresa_data?.nomeEmpresa || proposal.empresa_data?.nome) || 'Proposta de Energia'}
                  </Title>
                  <CustomToggle
                    id={`energy-proposal-${proposal.id}`}
                    checked={energyProposalsDone[proposal.id] || false}
                    onChange={(checked) => handleEnergyProposalToggle(proposal.id, checked)}
                  />
                </div>
              </div>
              
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {proposal.creator_username || 'N/A'}
                  </Text>
                </div>
                {proposal.lead_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Lead: {proposal.lead_name}
                    </Text>
                  </div>
                )}
                {proposal.empresa_data && (proposal.empresa_data.nomeEmpresa || proposal.empresa_data.nome) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Empresa: {proposal.empresa_data.nomeEmpresa || proposal.empresa_data.nome}
                    </Text>
                  </div>
                )}
                {proposal.service_quantities && Object.keys(proposal.service_quantities).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ThunderboltOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Serviços: {Object.keys(proposal.service_quantities).length} tipo(s)
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {new Date(proposal.created_at).toLocaleDateString('pt-PT')}
                  </Text>
                </div>
              </Space>

              <Button
                icon={<EyeOutlined />}
                onClick={() => handleViewEnergyProposal(proposal)}
                style={{ width: '100%' }}
              >
                Ver Detalhes
              </Button>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const renderProsegurForms = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
    }

    if (!data || sortedProsegurForms.length === 0) {
      return (
        <Empty
          description="Nenhum formulário Prosegur encontrado"
          style={{ padding: '40px' }}
        />
      );
    }

    return (
      <Row gutter={[16, 16]}>
        {sortedProsegurForms.map((form) => (
          <Col xs={24} sm={12} lg={12} xl={8} key={form.id}>
            <Card
              hoverable
              style={{
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                height: '100%'
              }}
              bodyStyle={{ padding: '20px' }}
            >
              <div style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Title level={5} style={{ margin: 0, color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                    {form.nome || form.lead_name || 'N/A'}
                  </Title>
                  <CustomToggle
                    id={`prosegur-form-${form.id}`}
                    checked={prosegurFormsDone[form.id] || false}
                    onChange={(checked) => handleProsegurFormToggle(form.id, checked)}
                  />
                </div>
              </div>
              
              <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <UserOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {form.creator_username || 'N/A'}
                  </Text>
                </div>
                {form.lead_name && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileTextOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      Lead: {form.lead_name}
                    </Text>
                  </div>
                )}
                {form.nif && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      NIF: {form.nif}
                    </Text>
                  </div>
                )}
                {form.email && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      {form.email}
                    </Text>
                  </div>
                )}
                {form.telemovel && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                    <Text style={{ color: '#595959', fontSize: '13px' }}>
                      {form.telemovel}
                    </Text>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarOutlined style={{ color: '#8c8c8c', fontSize: '14px' }} />
                  <Text style={{ color: '#595959', fontSize: '13px' }}>
                    {form.created_at ? new Date(form.created_at).toLocaleDateString('pt-PT') : 'N/A'}
                  </Text>
                </div>
              </Space>

              <Button
                icon={<EyeOutlined />}
                onClick={() => handleViewProsegurForm(form)}
                style={{ width: '100%' }}
              >
                Ver Detalhes
              </Button>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card
        style={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}
      >
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <TeamOutlined style={{ color: '#1890ff' }} />
              Validação e Processamento de Equipa
            </Title>
            {data && data.team_leader && (
              <Text type="secondary" style={{ marginTop: '8px', display: 'block' }}>
                Equipa: {data.team_leader.username || 'N/A'} • {data.team_members?.length || 0} membros
              </Text>
            )}
          </Col>
          <Col>
            {canSelectTeamLeader && (
              <Select
                value={selectedTeamLeaderId}
                onChange={setSelectedTeamLeaderId}
                placeholder="Selecionar líder de equipa..."
                style={{ width: 300 }}
                size="large"
                showSearch
                filterOption={(input, option) => {
                  const label = typeof option?.label === 'string' 
                    ? option.label 
                    : typeof option?.children === 'string'
                    ? option.children
                    : String(option?.label || option?.children || '');
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                optionFilterProp="children"
              >
                <Option value={null}>Minha Equipa</Option>
                {teamLeaders.map(leader => (
                  <Option key={leader.id} value={leader.id}>
                    {leader.username} ({leader.member_count} membros)
                  </Option>
                ))}
              </Select>
            )}
          </Col>
        </Row>

        <Input
          placeholder="Pesquisar..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: '24px', maxWidth: '400px' }}
          size="large"
        />

        <Collapse
          activeKey={activePanels}
          onChange={(keys) => setActivePanels(Array.isArray(keys) ? keys : [keys])}
          expandIconPosition="end"
          style={{ background: 'transparent' }}
        >
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <CheckCircleOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                <Text strong style={{ fontSize: '16px' }}>Validação Leads</Text>
                {data && data.validation_leads.length > 0 && (
                  <Badge count={data.validation_leads.length} style={{ marginLeft: '8px' }} />
                )}
              </div>
            }
            key="validation"
            style={{ marginBottom: '16px' }}
          >
            {renderValidationLeads()}
          </Panel>
          
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FileTextOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
                <Text strong style={{ fontSize: '16px' }}>NOS Contrato de Adesão</Text>
                {filteredNosContratos.length > 0 && (
                  <Badge count={filteredNosContratos.length} style={{ marginLeft: '8px' }} />
                )}
              </div>
            }
            key="nos"
            style={{ marginBottom: '16px' }}
          >
            {renderNosContratos()}
          </Panel>
          
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FileTextOutlined style={{ fontSize: '18px', color: '#722ed1' }} />
                <Text strong style={{ fontSize: '16px' }}>NOS Securitas Contrato de Adesão</Text>
                {filteredNosSecuritasContratos.length > 0 && (
                  <Badge count={filteredNosSecuritasContratos.length} style={{ marginLeft: '8px' }} />
                )}
              </div>
            }
            key="nos-securitas"
            style={{ marginBottom: '16px' }}
          >
            {renderNosSecuritasContratos()}
          </Panel>
          
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FileTextOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                <Text strong style={{ fontSize: '16px' }}>Altice Forms</Text>
                {data && data.altice_forms.length > 0 && (
                  <Badge count={data.altice_forms.length} style={{ marginLeft: '8px' }} />
                )}
              </div>
            }
            key="altice"
            style={{ marginBottom: '16px' }}
          >
            {renderAlticeForms()}
          </Panel>
          
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <ThunderboltOutlined style={{ fontSize: '18px', color: '#faad14' }} />
                <Text strong style={{ fontSize: '16px' }}>Pedidos de Proposta Energia/Solar</Text>
                {data && data.energy_proposals && data.energy_proposals.length > 0 && (
                  <Badge count={data.energy_proposals.length} style={{ marginLeft: '8px' }} />
                )}
              </div>
            }
            key="energy-proposals"
            style={{ marginBottom: '16px' }}
          >
            {renderEnergyProposals()}
          </Panel>
          
          <Panel
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <FileTextOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
                <Text strong style={{ fontSize: '16px' }}>Formulários Prosegur</Text>
                {data && data.prosegur_forms && data.prosegur_forms.length > 0 && (
                  <Badge count={data.prosegur_forms.length} style={{ marginLeft: '8px' }} />
                )}
              </div>
            }
            key="prosegur-forms"
            style={{ marginBottom: '16px' }}
          >
            {renderProsegurForms()}
          </Panel>
        </Collapse>
      </Card>

      {/* Modals */}
      {selectedLeadForDocuments && (
        <LeadDocumentsModal
          open={documentsModalVisible}
          onClose={() => {
            setDocumentsModalVisible(false);
            setSelectedLeadForDocuments(null);
          }}
          leadId={selectedLeadForDocuments.id}
          leadName={selectedLeadForDocuments.name || 'Lead'}
        />
      )}

      {selectedLeadForEmail && (
        <EmailModal
          visible={emailModalVisible}
          onClose={() => {
            setEmailModalVisible(false);
            setSelectedLeadForEmail(null);
          }}
          lead={selectedLeadForEmail}
        />
      )}

      {/* NOS Contrato Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span>Detalhes do Contrato de Adesão</span>
            {selectedNosContrato && (
              <Tag color={selectedNosContrato.is_securitas ? 'purple' : 'blue'}>
                {selectedNosContrato.contract_type || (selectedNosContrato.is_securitas ? 'NOS Securitas' : 'NOS')}
              </Tag>
            )}
          </div>
        }
        open={nosDetailModalVisible}
        onCancel={() => {
          setNosDetailModalVisible(false);
          setSelectedNosContrato(null);
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        {selectedNosContrato && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Informações do Gestor e Segmento" size="small" style={{ marginBottom: '16px' }}>
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="Gestor Responsável" span={2}>
                      {selectedNosContrato.gestor_responsavel || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Segmento NOS">
                      {selectedNosContrato.segmento_nos || 'N/A'}
                    </Descriptions.Item>
                    {/* Only show categoria, tecnologia, tipologia for regular NOS */}
                    {!selectedNosContrato.is_securitas && selectedNosContrato.categoria && (
                      <Descriptions.Item label="Categoria">
                        {selectedNosContrato.categoria}
                      </Descriptions.Item>
                    )}
                    {!selectedNosContrato.is_securitas && selectedNosContrato.tecnologia && (
                      <Descriptions.Item label="Tecnologia">
                        {selectedNosContrato.tecnologia}
                      </Descriptions.Item>
                    )}
                    {!selectedNosContrato.is_securitas && selectedNosContrato.tipologia && (
                      <Descriptions.Item label="Tipologia">
                        {selectedNosContrato.tipologia}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Col>

              {/* NOS Securitas - Produtos e Serviços */}
              {selectedNosContrato.is_securitas && (
                <Col span={24}>
                  <Card title="Produtos e Serviços" size="small" style={{ marginBottom: '16px' }}>
                    <Descriptions column={2} size="small" bordered>
                      <Descriptions.Item label="Pacote Comercial" span={2}>
                        {selectedNosContrato.pacote_comercial || selectedNosContrato.securitas_fields?.pacote_comercial || 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Detetor de Movimento">
                        {selectedNosContrato.equipamento_obrigatorio_detetor_movimento === 'true' || selectedNosContrato.equipamento_obrigatorio_detetor_movimento === true || selectedNosContrato.equipamento_obrigatorio_detetor_movimento === 'on' ? 'Sim' : 
                         selectedNosContrato.equipamento_obrigatorio_detetor_movimento === 'false' || selectedNosContrato.equipamento_obrigatorio_detetor_movimento === false ? 'Não' : 
                         selectedNosContrato.equipamento_obrigatorio_detetor_movimento || 'Não'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Detetor de Movimento com Câmara">
                        {selectedNosContrato.equipamento_obrigatorio_detetor_movimento_camera === 'true' || selectedNosContrato.equipamento_obrigatorio_detetor_movimento_camera === true || selectedNosContrato.equipamento_obrigatorio_detetor_movimento_camera === 'on' ? 'Sim' : 
                         selectedNosContrato.equipamento_obrigatorio_detetor_movimento_camera === 'false' || selectedNosContrato.equipamento_obrigatorio_detetor_movimento_camera === false ? 'Não' : 
                         selectedNosContrato.equipamento_obrigatorio_detetor_movimento_camera || 'Não'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Painel de Controlo">
                        {selectedNosContrato.equipamento_obrigatorio_painel_controlo === 'true' || selectedNosContrato.equipamento_obrigatorio_painel_controlo === true || selectedNosContrato.equipamento_obrigatorio_painel_controlo === 'on' ? 'Sim' : 
                         selectedNosContrato.equipamento_obrigatorio_painel_controlo === 'false' || selectedNosContrato.equipamento_obrigatorio_painel_controlo === false ? 'Não' : 
                         selectedNosContrato.equipamento_obrigatorio_painel_controlo || 'Não'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Placa Dissuasora">
                        {selectedNosContrato.equipamento_obrigatorio_placa_dissuasora === 'true' || selectedNosContrato.equipamento_obrigatorio_placa_dissuasora === true || selectedNosContrato.equipamento_obrigatorio_placa_dissuasora === 'on' ? 'Sim' : 
                         selectedNosContrato.equipamento_obrigatorio_placa_dissuasora === 'false' || selectedNosContrato.equipamento_obrigatorio_placa_dissuasora === false ? 'Não' : 
                         selectedNosContrato.equipamento_obrigatorio_placa_dissuasora || 'Não'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Sensor de Porta ou Janela">
                        {selectedNosContrato.equipamento_obrigatorio_sensor_porta_janela === 'true' || selectedNosContrato.equipamento_obrigatorio_sensor_porta_janela === true || selectedNosContrato.equipamento_obrigatorio_sensor_porta_janela === 'on' ? 'Sim' : 
                         selectedNosContrato.equipamento_obrigatorio_sensor_porta_janela === 'false' || selectedNosContrato.equipamento_obrigatorio_sensor_porta_janela === false ? 'Não' : 
                         selectedNosContrato.equipamento_obrigatorio_sensor_porta_janela || 'Não'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Outros Equipamentos" span={2}>
                        {(selectedNosContrato.outros_equipamentos || selectedNosContrato.securitas_fields?.outros_equipamentos) ? (
                          (() => {
                            const value = selectedNosContrato.outros_equipamentos || selectedNosContrato.securitas_fields?.outros_equipamentos;
                            return Array.isArray(value) 
                              ? value.join(', ')
                              : value;
                          })()
                        ) : 'N/A'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Validar Necessidades Câmaras Internet Fixa">
                        {selectedNosContrato.validar_necessidades_cameras_internet_fixa === 'true' || selectedNosContrato.validar_necessidades_cameras_internet_fixa === true || selectedNosContrato.validar_necessidades_cameras_internet_fixa === 'on' ? 'Sim' : 
                         selectedNosContrato.validar_necessidades_cameras_internet_fixa === 'false' || selectedNosContrato.validar_necessidades_cameras_internet_fixa === false ? 'Não' : 
                         selectedNosContrato.validar_necessidades_cameras_internet_fixa || 'Não'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Validar Necessidades Grau 3">
                        {selectedNosContrato.validar_necessidades_grau_3 === 'true' || selectedNosContrato.validar_necessidades_grau_3 === true || selectedNosContrato.validar_necessidades_grau_3 === 'on' ? 'Sim' : 
                         selectedNosContrato.validar_necessidades_grau_3 === 'false' || selectedNosContrato.validar_necessidades_grau_3 === false ? 'Não' : 
                         selectedNosContrato.validar_necessidades_grau_3 || 'Não'}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                </Col>
              )}

              {/* Regular NOS - Detalhes do Pacote */}
              {!selectedNosContrato.is_securitas && (
                <Col span={24}>
                  <Card title="Detalhes do Pacote" size="small" style={{ marginBottom: '16px' }}>
                    <Descriptions column={1} size="small" bordered>
                      {selectedNosContrato.codigo_pacote && (
                        <Descriptions.Item label="Código do Pacote">
                          {selectedNosContrato.codigo_pacote}
                        </Descriptions.Item>
                      )}
                      {selectedNosContrato.descricao_pacote && (
                        <Descriptions.Item label="Descrição do Pacote">
                          {selectedNosContrato.descricao_pacote}
                        </Descriptions.Item>
                      )}
                      {selectedNosContrato.cartoes_portar && (
                        <Descriptions.Item label="Cartões a Portar">
                          {selectedNosContrato.cartoes_portar}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </Col>
              )}

              <Col span={24}>
                <Card title="Dados do Cliente" size="small" style={{ marginBottom: '16px' }}>
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="Nome do Cliente" span={2}>
                      {selectedNosContrato.nome_cliente || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="NIF">
                      {selectedNosContrato.nif || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="CC/BI/Passaporte">
                      {selectedNosContrato.cc_bi_passaporte || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Nacionalidade">
                      {selectedNosContrato.nacionalidade || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Morada de Instalação" span={2}>
                      {selectedNosContrato.morada_instalacao || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Código Postal">
                      {selectedNosContrato.codigo_postal_1 && selectedNosContrato.codigo_postal_2 
                        ? `${selectedNosContrato.codigo_postal_1}-${selectedNosContrato.codigo_postal_2}`
                        : 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Localidade">
                      {selectedNosContrato.localidade || 'N/A'}
                    </Descriptions.Item>
                    {selectedNosContrato.coordenadas && (
                      <Descriptions.Item label="Coordenadas">
                        {selectedNosContrato.coordenadas}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label="Telefone">
                      {selectedNosContrato.telefone_contacto || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">
                      {selectedNosContrato.email || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col span={24}>
                <Card title="Pagamento, Equipamentos e Documentos" size="small" style={{ marginBottom: '16px' }}>
                  <Descriptions column={2} size="small" bordered>
                    {selectedNosContrato.debito_direto_iban && (
                      <Descriptions.Item label="IBAN">
                        {selectedNosContrato.debito_direto_iban}
                      </Descriptions.Item>
                    )}
                    {selectedNosContrato.nome_titular_debito && (
                      <Descriptions.Item label="Titular do Débito">
                        {selectedNosContrato.nome_titular_debito}
                      </Descriptions.Item>
                    )}
                    {selectedNosContrato.equipamentos_adicionais && (
                      <Descriptions.Item label="Equipamentos Adicionais" span={2}>
                        {selectedNosContrato.equipamentos_adicionais}
                      </Descriptions.Item>
                    )}
                    {selectedNosContrato.descricao_ofertas && (
                      <Descriptions.Item label="Descrição das Ofertas" span={2}>
                        {selectedNosContrato.descricao_ofertas}
                      </Descriptions.Item>
                    )}
                    {selectedNosContrato.data_horario_preferencial && (
                      <Descriptions.Item label="Data/Horário Preferencial" span={2}>
                        {selectedNosContrato.data_horario_preferencial}
                      </Descriptions.Item>
                    )}
                    {selectedNosContrato.observacoes && (
                      <Descriptions.Item label="Observações" span={2}>
                        {selectedNosContrato.observacoes}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Col>

              {/* Uploaded Files */}
              {selectedNosContrato.uploaded_files && selectedNosContrato.uploaded_files.length > 0 && (
                <Col span={24}>
                  <Card title="Documentos Anexos" size="small" style={{ marginBottom: '16px' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {selectedNosContrato.uploaded_files.map((file: any) => (
                        <div key={file.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                            <div>
                              <Text strong>{file.filename}</Text>
                              {!file.exists && (
                                <Tag color="red" style={{ marginLeft: '8px' }}>Ficheiro não encontrado</Tag>
                              )}
                            </div>
                          </div>
                          {file.exists && (
                            <Button
                              type="primary"
                              icon={<DownloadOutlined />}
                              href={`/api${file.download_url}`}
                              target="_blank"
                            >
                              Descarregar
                            </Button>
                          )}
                        </div>
                      ))}
                    </Space>
                  </Card>
                </Col>
              )}

              <Col span={24}>
                <Card title="Informações do Sistema" size="small">
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="Utilizador">
                      {selectedNosContrato.user_username || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Lead">
                      {selectedNosContrato.lead_name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">
                      <Tag color={selectedNosContrato.status === 'pending' ? 'orange' : selectedNosContrato.status === 'approved' ? 'green' : 'red'}>
                        {selectedNosContrato.status === 'pending' ? 'Pendente' : selectedNosContrato.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Data de Criação">
                      {new Date(selectedNosContrato.created_at).toLocaleString('pt-PT')}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Altice Form Detail Modal */}
      <Modal
        title="Detalhes do Formulário Altice"
        open={alticeDetailModalVisible}
        onCancel={() => {
          setAlticeDetailModalVisible(false);
          setSelectedAlticeForm(null);
        }}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        {selectedAlticeForm ? (
          <AlticeSaleDetailView
            sale={selectedAlticeForm}
            showSystemInfo
            creatorUsername={selectedAlticeForm.creator_username}
            leadName={selectedAlticeForm.lead_name}
            maxHeight="75vh"
          />
        ) : null}
      </Modal>

      {/* NOS Email Modal - Simplified version for now */}
      <Modal
        title="Enviar Email - Contrato de Adesão NOS"
        open={nosEmailModalVisible}
        onCancel={() => {
          setNosEmailModalVisible(false);
          setSelectedNosForEmail(null);
        }}
        footer={null}
        width={700}
      >
        <Text type="secondary">
          Funcionalidade de email específica para NOS será implementada em breve.
          Por enquanto, pode usar a funcionalidade de email geral através do lead associado.
        </Text>
      </Modal>

      {/* Energy Proposal Detail Modal */}
      <Modal
        title="Detalhes do Pedido de Proposta Energia/Solar"
        open={energyProposalDetailModalVisible}
        onCancel={() => {
          setEnergyProposalDetailModalVisible(false);
          setSelectedEnergyProposal(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setEnergyProposalDetailModalVisible(false);
            setSelectedEnergyProposal(null);
          }}>
            Fechar
          </Button>
        ]}
        width={1200}
        style={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {selectedEnergyProposal && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              {/* Empresa Data */}
              {selectedEnergyProposal.empresa_data && (
                <Col span={24}>
                  <Card title="Informações da Empresa" size="small" style={{ marginBottom: '16px' }}>
                    <Descriptions column={2} size="small" bordered>
                      {selectedEnergyProposal.empresa_data.nomeEmpresa && (
                        <Descriptions.Item label="Nome da Empresa">
                          {selectedEnergyProposal.empresa_data.nomeEmpresa}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.nifEmpresa && (
                        <Descriptions.Item label="NIF da Empresa">
                          {selectedEnergyProposal.empresa_data.nifEmpresa}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.moradaFornecimento && (
                        <Descriptions.Item label="Morada de Fornecimento" span={2}>
                          {selectedEnergyProposal.empresa_data.moradaFornecimento}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.moradaCorrespondencia && (
                        <Descriptions.Item label="Morada de Correspondência" span={2}>
                          {selectedEnergyProposal.empresa_data.moradaCorrespondencia}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.regiao && (
                        <Descriptions.Item label="Região">
                          {selectedEnergyProposal.empresa_data.regiao}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.cae && (
                        <Descriptions.Item label="CAE">
                          {selectedEnergyProposal.empresa_data.cae}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.nomeRepresentante && (
                        <Descriptions.Item label="Nome do Representante">
                          {selectedEnergyProposal.empresa_data.nomeRepresentante}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.contactoRepresentante && (
                        <Descriptions.Item label="Contacto do Representante">
                          {selectedEnergyProposal.empresa_data.contactoRepresentante}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.emailRepresentante && (
                        <Descriptions.Item label="Email do Representante">
                          {selectedEnergyProposal.empresa_data.emailRepresentante}
                        </Descriptions.Item>
                      )}
                      {selectedEnergyProposal.empresa_data.emailEmpresa && (
                        <Descriptions.Item label="Email da Empresa">
                          {selectedEnergyProposal.empresa_data.emailEmpresa}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                </Col>
              )}

              {/* Service Instances and Details */}
              {selectedEnergyProposal.service_instances && selectedEnergyProposal.service_instances.length > 0 && (
                <Col span={24}>
                  <Card title="Instâncias de Serviços" size="small" style={{ marginBottom: '16px' }}>
                    <Collapse>
                      {selectedEnergyProposal.service_instances.map((instance: any, index: number) => {
                        const instanceDetails = selectedEnergyProposal.service_details?.[instance.instanceId] || {};
                        return (
                          <Panel 
                            key={instance.instanceId} 
                            header={`${SERVICE_NAMES[instance.service] || instance.service} - Instância ${instance.instanceNumber}`}
                          >
                            <Descriptions column={2} size="small" bordered>
                              {Object.entries(instanceDetails).map(([key, value]: [string, any]) => {
                                // Skip download URL keys and specific name fields
                                if (key.endsWith('_download_url') || key === 'printTelhado_name' || key === 'fatura_name') {
                                  return null;
                                }
                                
                                // Check if this is a file field (value is a file path or there's a download URL)
                                const isFileField = typeof value === 'string' && value.startsWith('energy_proposals/');
                                const downloadUrl = instanceDetails[`${key}_download_url`];
                                const hasDownloadUrl = !!downloadUrl;
                                
                                // Show download button if it's a file field or has a download URL
                                if (isFileField || hasDownloadUrl) {
                                  // Extract filename from value or path
                                  let filename = 'documento.pdf';
                                  let filePath = value;
                                  
                                  if (downloadUrl) {
                                    try {
                                      const urlObj = new URL(downloadUrl);
                                      filePath = urlObj.searchParams.get('path') || value;
                                    } catch (e) {
                                      // If URL parsing fails, use value as path
                                      filePath = value;
                                    }
                                  }
                                  
                                  // Extract filename from path
                                  if (filePath && typeof filePath === 'string') {
                                    const pathParts = filePath.split('/');
                                    filename = pathParts[pathParts.length - 1] || 'documento.pdf';
                                    // Remove UUID prefix if present
                                    if (filename.includes('_') && filename.split('_').length > 1) {
                                      filename = filename.split('_').slice(1).join('_');
                                    }
                                  }
                                  
                                  return (
                                    <Descriptions.Item key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <FilePdfOutlined style={{ color: '#ff4d4f' }} />
                                        <Text>{filename}</Text>
                                        <Button
                                          type="link"
                                          icon={<DownloadOutlined />}
                                          onClick={async () => {
                                            try {
                                              if (!filePath) {
                                                message.error('Caminho do ficheiro não encontrado');
                                                return;
                                              }
                                              
                                              const response = await apiService.downloadEnergyProposalFile(filePath, filename);
                                              
                                              if (response.success) {
                                                // Create a download link and trigger download
                                                const blob = response.data;
                                                const url = window.URL.createObjectURL(blob);
                                                const link = document.createElement('a');
                                                link.href = url;
                                                link.download = filename;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                                window.URL.revokeObjectURL(url);
                                                
                                                message.success('Download iniciado');
                                              } else {
                                                message.error('Erro ao fazer download do ficheiro');
                                              }
                                            } catch (error) {
                                              console.error('Error downloading file:', error);
                                              message.error('Erro ao fazer download do ficheiro');
                                            }
                                          }}
                                          size="small"
                                        >
                                          Descarregar
                                        </Button>
                                      </div>
                                    </Descriptions.Item>
                                  );
                                }
                                
                                // Regular field (not a file)
                                return (
                                  <Descriptions.Item key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}>
                                    {Array.isArray(value) ? value.join(', ') : String(value || 'N/A')}
                                  </Descriptions.Item>
                                );
                              })}
                            </Descriptions>
                          </Panel>
                        );
                      })}
                    </Collapse>
                  </Card>
                </Col>
              )}

              {/* System Information */}
              <Col span={24}>
                <Card title="Informações do Sistema" size="small">
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="Criado por">
                      {selectedEnergyProposal.creator_username || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Lead">
                      {selectedEnergyProposal.lead_name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data de Criação" span={2}>
                      {new Date(selectedEnergyProposal.created_at).toLocaleString('pt-PT')}
                    </Descriptions.Item>
                    {selectedEnergyProposal.updated_at && (
                      <Descriptions.Item label="Última Atualização" span={2}>
                        {new Date(selectedEnergyProposal.updated_at).toLocaleString('pt-PT')}
                      </Descriptions.Item>
                    )}
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* Prosegur Form Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FileTextOutlined style={{ fontSize: '18px', color: '#52c41a' }} />
            <span>Detalhes do Formulário Prosegur</span>
          </div>
        }
        open={prosegurFormDetailModalVisible}
        onCancel={() => {
          setProsegurFormDetailModalVisible(false);
          setSelectedProsegurForm(null);
        }}
        footer={null}
        width={800}
        style={{ top: 20 }}
      >
        {selectedProsegurForm && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card title="Dados do Cliente" size="small" style={{ marginBottom: '16px' }}>
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="Nome" span={2}>
                      {selectedProsegurForm.nome || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="NIF">
                      {selectedProsegurForm.nif || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Telemóvel">
                      {selectedProsegurForm.telemovel || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="E-mail" span={2}>
                      {selectedProsegurForm.email || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Morada de Instalação" span={2}>
                      {selectedProsegurForm.morada_instalacao || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="IBAN" span={2}>
                      {selectedProsegurForm.iban || 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              <Col span={24}>
                <Card title="Informações do Sistema" size="small">
                  <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label="Criado por">
                      {selectedProsegurForm.creator_username || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Lead">
                      {selectedProsegurForm.lead_name || 'N/A'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Data de Criação" span={2}>
                      {selectedProsegurForm.created_at ? new Date(selectedProsegurForm.created_at).toLocaleString('pt-PT') : 'N/A'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TeamValidationProcessing;

